import React, { useEffect, useMemo, useState } from 'react';
import {
    FaBullhorn, FaComments, FaCommentDots,
    FaChartLine, FaUsers, FaRobot, FaExclamationTriangle, FaCheckCircle,
    FaSync, FaInstagram, FaFacebook, FaTiktok, FaYoutube, FaTwitter, FaGlobe,
    FaLightbulb, FaUserCheck, FaEye, FaTrophy, FaFire, FaImage,
    FaHeart, FaPlay, FaExternalLinkAlt, FaTimes,
} from 'react-icons/fa';
import {
    Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, ArcElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import { socialApi, proxyImage } from './api';
import { SOCIAL_READONLY } from './analyticsShared';
import './generalDashboard.css';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement,
    BarElement, ArcElement, Tooltip, Legend, Filler);

/**
 * Dashboard General — sala de guerra competitiva (Propias vs. De interés).
 *
 * Eje central: compara TUS cuentas ('own') contra las cuentas de interés /
 * competencia ('interest'), usando social_accounts.account_kind. Agrega del lado
 * del cliente /api/social/accounts + /api/social/metrics/overview (+ /metrics/posts
 * para las miniaturas) por cuenta y bucketea por kind. No requiere endpoint nuevo.
 *
 * Diseño inspirado en el módulo "Redes Sociales": gráficas suavizadas, modales de
 * detalle (cuenta y publicación) y miniaturas.
 *
 * Honestidad como feature: el sentimiento NO se ingiere automáticamente (es un
 * paso manual por post). Las cuentas de interés casi nunca se analizan, así que los
 * widgets de sentimiento se GUARDAN: si un grupo no tiene comentarios analizados,
 * muestran "sin análisis" en vez de 0 (un 0 se leería como neutral/sano, engañoso).
 */

const NAVY = '#073C5C';
const ORANGE = '#F68D2E';

const PLAT = {
    instagram: { label: 'Instagram', Icon: FaInstagram, color: '#E1306C' },
    facebook: { label: 'Facebook', Icon: FaFacebook, color: '#1877F2' },
    tiktok: { label: 'TikTok', Icon: FaTiktok, color: '#111827' },
    youtube: { label: 'YouTube', Icon: FaYoutube, color: '#FF0000' },
    twitter: { label: 'Twitter / X', Icon: FaTwitter, color: '#1DA1F2' },
};
const REAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'youtube'];

// Eje competitivo: cuentas propias vs. de interés (competencia).
const KIND = {
    own: { label: 'Propias', Icon: FaUserCheck, color: NAVY, tint: '#e0f2fe' },
    interest: { label: 'De interés', Icon: FaEye, color: '#7c3aed', tint: '#ede9fe' },
};
const kindOf = (a) => (a?.account_kind === 'interest' ? 'interest' : 'own');
const sentDot = { pos: '#16a34a', neu: '#d97706', neg: '#dc2626' };

const RANGES = [
    { label: 'Últimos 30 días', days: 30 },
    { label: 'Últimos 90 días', days: 90 },
    { label: 'Último año', days: 365 },
    { label: 'Todo', days: 0 },
];

// ── helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    const x = Number(n);
    if (Number.isNaN(x)) return '—';
    if (Math.abs(x) >= 1e6) return (x / 1e6).toFixed(1) + 'M';
    if (Math.abs(x) >= 1e3) return (x / 1e3).toFixed(1) + 'K';
    return String(x);
};
const nssColor = (v) => (v == null ? '#94a3b8' : v > 15 ? '#16a34a' : v < -15 ? '#dc2626' : '#d97706');
const round1 = (x) => Math.round(x * 10) / 10;
const dateFromDays = (days) => {
    if (!days) return '';
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().slice(0, 10);
};
// Media móvil para suavizar series (ignora huecos). Igual que en Redes Sociales.
const movingAvg = (vals, window) => {
    if (!window || window <= 1) return vals;
    const half = Math.floor(window / 2);
    return vals.map((_, i) => {
        let sum = 0, n = 0;
        for (let j = i - half; j <= i + half; j++) {
            const v = vals[j];
            if (v != null) { sum += v; n += 1; }
        }
        return n ? Math.round((sum / n) * 100) / 100 : null;
    });
};
const acctHandle = (a) => (a && a.track_by === 'keyword' && a.platform === 'tiktok'
    ? a.username : `@${a?.username}`);
// Suma N días a una fecha 'YYYY-MM-DD' (UTC, para no desfasar por zona horaria).
const addDaysStr = (dateStr, n) => {
    const d = new Date(`${dateStr}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

// ── Miniatura con fallback (CDN caduca / 403) ─────────────────────────────────
function Thumb({ src, className }) {
    const [ok, setOk] = useState(true);
    return src && ok
        ? <img className={className} src={proxyImage(src)} alt="" loading="lazy" onError={() => setOk(false)} />
        : <div className={`gd-thumb-ph ${className || ''}`}><FaImage /></div>;
}

// ── Modal de detalle de una publicación (solo lectura) ────────────────────────
function PostModal({ username, post, onClose }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [imgOk, setImgOk] = useState(true);
    useEffect(() => {
        let on = true;
        setLoading(true);
        socialApi.postDetail(username, post.id)
            .then((r) => { if (on) setDetail(r.detail); })
            .catch(() => {})
            .finally(() => { if (on) setLoading(false); });
        return () => { on = false; };
    }, [username, post.id]);

    const pv = detail?.post || post;
    const sent = detail?.sentiment;
    const comments = detail?.comments || [];
    const total = sent ? ((sent.pos + sent.neu + sent.neg) || 1) : 1;
    const pct = (x) => Math.round((x / total) * 100);
    const netName = post._platform === 'facebook' ? 'Facebook' : post._platform === 'tiktok' ? 'TikTok' : post._platform === 'youtube' ? 'YouTube' : 'Instagram';

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="sa-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="sa-modal-grid">
                    <div className="sa-modal-preview">
                        {pv.thumbnail && imgOk
                            ? <img src={proxyImage(pv.thumbnail)} alt="" onError={() => setImgOk(false)} />
                            : <div className="gd-thumb-ph big"><FaImage /></div>}
                        <div className="sa-modal-meta">
                            <span>{(pv.posted_at || '').slice(0, 10)} · {pv.content_type || 'Post'}{pv.is_paid ? ' 💰' : ''}</span>
                            <span><FaHeart /> {fmt(pv.likes)} · <FaCommentDots /> {fmt(pv.comments_count)}
                                {pv.views != null ? <> · <FaPlay /> {fmt(pv.views)}</> : null}</span>
                            {post._handle && <span style={{ color: '#64748b' }}>{acctHandle({ username: post._handle, platform: post._platform })}</span>}
                            {pv.url && <a href={pv.url} target="_blank" rel="noreferrer">Ver en {netName} <FaExternalLinkAlt /></a>}
                        </div>
                        <p className="sa-modal-caption">{pv.caption || '(sin descripción)'}</p>
                    </div>

                    <div className="sa-modal-analysis">
                        {loading && <p className="gd-subtitle">Cargando…</p>}
                        {sent ? (
                            <div className="sa-modal-sent">
                                <div className="sa-sent-head">
                                    <span className="sa-sent-nss" style={{ color: nssColor(sent.nss) }}>NSS {sent.nss}</span>
                                    <span className="sa-sent-sub">{sent.comments_analyzed} comentarios · polarización {sent.polarization}</span>
                                </div>
                                <div className="sa-sent-bar">
                                    <div style={{ width: `${pct(sent.pos)}%`, background: sentDot.pos }} />
                                    <div style={{ width: `${pct(sent.neu)}%`, background: sentDot.neu }} />
                                    <div style={{ width: `${pct(sent.neg)}%`, background: sentDot.neg }} />
                                </div>
                                {sent.summary && <p className="sa-sent-summary">{sent.summary}</p>}
                            </div>
                        ) : (!loading && <p className="gd-subtitle">Sin análisis de sentimiento para esta publicación. Analízalo desde <b>Redes Sociales</b>.</p>)}

                        {comments.length > 0 && (
                            <div className="sa-comments">
                                <h5>Comentarios ({comments.length})</h5>
                                {comments.map((c, i) => (
                                    <div key={i} className="sa-comment">
                                        {c.sentiment && <span className="sa-comment-dot" style={{ background: sentDot[c.sentiment] }} title={c.theme || ''} />}
                                        <div className="sa-comment-body">
                                            <span className="sa-comment-user">@{c.commenter || 'usuario'}</span>
                                            <span className="sa-comment-text">{c.text}</span>
                                            <span className="sa-comment-meta"><FaHeart /> {fmt(c.likes)}{c.theme ? ` · ${c.theme}` : ''}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ── Modal de detalle de una cuenta (KPIs + sentimiento + mejores posts) ───────
function AccountModal({ account, overview, posts, onOpenPost, onClose, noAnim }) {
    const meta = PLAT[account.platform] || {};
    const Icon = meta.Icon || FaGlobe;
    const k = KIND[kindOf(account)];
    const rg = overview?.reach_growth || {};
    const eng = overview?.engagement || {};
    const sent = overview?.sentiment || {};
    const flow = overview?.sentiment_flow || [];
    const themes = (overview?.themes || []).slice(0, 6);
    const best = [...(posts || [])].sort((a, b) => (b.er ?? -1) - (a.er ?? -1)).slice(0, 4);
    const hasSent = (sent.analyzed || 0) > 0 && flow.length > 0;
    const nssLine = movingAvg(flow.map((b) => b.nss), 3);

    const kpis = [
        { lbl: 'Seguidores', val: fmt(account.followers ?? rg.followers), ic: <FaUsers /> },
        { lbl: 'Engagement', val: eng.avg_engagement_rate != null ? `${round1(eng.avg_engagement_rate)}%` : '—', ic: <FaChartLine /> },
        { lbl: 'NSS', val: sent.analyzed ? sent.nss : '—', ic: <FaComments />, color: sent.analyzed ? nssColor(sent.nss) : undefined },
        { lbl: 'Posts', val: fmt(eng.posts), ic: <FaBullhorn /> },
        { lbl: 'Crecimiento', val: rg.followers_growth ? `${rg.followers_growth > 0 ? '▲' : '▼'} ${fmt(Math.abs(rg.followers_growth))}` : '—', ic: <FaFire /> },
    ];

    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="gd-acct-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="gd-acct-head">
                    <span className="gd-acct-ic" style={{ color: meta.color }}><Icon /></span>
                    <div className="gd-acct-id">
                        <h3>{acctHandle(account)}</h3>
                        <span className="gd-kind-pill" style={{ color: k.color, borderColor: k.color }}>{k.label}</span>
                    </div>
                </div>

                <div className="gd-acct-kpis">
                    {kpis.map((m) => (
                        <div className="gd-acct-kpi" key={m.lbl}>
                            <span className="gd-acct-kpi-ic">{m.ic}</span>
                            <span className="gd-acct-kpi-val" style={{ color: m.color }}>{m.val}</span>
                            <span className="gd-acct-kpi-lbl">{m.lbl}</span>
                        </div>
                    ))}
                </div>

                <h5>Sentimiento en el tiempo (NSS)</h5>
                {hasSent ? (
                    <div className="gd-acct-chart">
                        <Line
                            data={{
                                labels: flow.map((b) => (b.bucket || '').slice(0, 10)),
                                datasets: [{
                                    label: 'NSS', data: nssLine, borderColor: NAVY,
                                    backgroundColor: 'rgba(7,60,92,.10)', fill: true, tension: 0.4,
                                    pointRadius: 2, pointBackgroundColor: NAVY, borderWidth: 2, spanGaps: true,
                                }],
                            }}
                            options={{
                                responsive: true, maintainAspectRatio: false, animation: noAnim,
                                plugins: { legend: { display: false } },
                                scales: { y: { min: -100, max: 100, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
                            }}
                        />
                    </div>
                ) : <p className="gd-note">Sin comentarios analizados para esta cuenta.</p>}

                {themes.length > 0 && (
                    <>
                        <h5>Temas más comentados</h5>
                        <div className="gd-theme-chips">
                            {themes.map((t, i) => (
                                <span key={i} className="gd-theme-chip" style={{ borderColor: nssColor(t.nss) }}>
                                    {t.theme} <em style={{ color: nssColor(t.nss) }}>{t.nss}</em>
                                </span>
                            ))}
                        </div>
                    </>
                )}

                {best.length > 0 && (
                    <>
                        <h5>Mejores publicaciones</h5>
                        <div className="gd-acct-posts">
                            {best.map((p) => (
                                <button className="gd-mini-post" key={p.id}
                                    onClick={() => onOpenPost({ ...p, _platform: account.platform, _handle: account.username })}>
                                    <Thumb src={p.thumbnail} className="gd-mini-thumb" />
                                    <span className="gd-mini-er">ER {p.er ?? '—'}%</span>
                                </button>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ── Tarjeta de publicación con miniatura (usada en "Mejores" y en el drill-down) ─
function TopPostCard({ post, onOpen }) {
    const meta = PLAT[post._platform] || {};
    const PIcon = meta.Icon || FaGlobe;
    return (
        <button className="gd-top-card" onClick={() => onOpen(post)}>
            <div className="gd-top-thumb">
                <Thumb src={post.thumbnail} />
                <span className="gd-top-kind" style={{ background: KIND[post._kind].color }}>{KIND[post._kind].label}</span>
                {post.nss != null && <span className="gd-top-nss" style={{ background: nssColor(post.nss) }}>NSS {post.nss}</span>}
            </div>
            <div className="gd-top-body">
                <span className="gd-top-handle"><PIcon style={{ color: meta.color }} /> {acctHandle({ username: post._handle, platform: post._platform })}</span>
                <div className="gd-top-metrics">
                    <span className="gd-top-er">ER {post.er ?? '—'}%</span>
                    <span><FaHeart /> {fmt(post.likes)}</span>
                    <span><FaCommentDots /> {fmt(post.comments_count)}</span>
                </div>
            </div>
        </button>
    );
}

// ── Modal: publicaciones publicadas en la fecha/semana clickeada de la gráfica ──
function DayPostsModal({ date, bucket, posts, onOpenPost, onClose }) {
    const end = addDaysStr(date, bucket === 'day' ? 1 : 7);
    const list = posts
        .filter((p) => { const d = (p.posted_at || '').slice(0, 10); return d >= date && d < end; })
        .sort((a, b) => (b.er ?? -1) - (a.er ?? -1));
    return (
        <div className="sa-modal-overlay" onClick={onClose}>
            <div className="gd-acct-modal" onClick={(e) => e.stopPropagation()}>
                <button className="sa-modal-close" onClick={onClose}><FaTimes /></button>
                <div className="gd-acct-head">
                    <span className="gd-acct-ic" style={{ color: '#F68D2E' }}><FaFire /></span>
                    <div className="gd-acct-id">
                        <h3>Publicaciones del {date}{bucket === 'week' ? ` al ${addDaysStr(date, 6)}` : ''}</h3>
                        <span className="gd-muted-h">{list.length} {list.length === 1 ? 'publicación' : 'publicaciones'}</span>
                    </div>
                </div>
                {list.length === 0 ? (
                    <p className="gd-note">No se publicaron posts en esta fecha. La línea de NSS refleja el sentimiento de los <b>comentarios recibidos</b> ese día, que pueden ser sobre publicaciones anteriores.</p>
                ) : (
                    ['own', 'interest'].map((kk) => {
                        const grp = list.filter((p) => p._kind === kk);
                        if (grp.length === 0) return null;
                        return (
                            <div className="gd-day-group" key={kk}>
                                <h5 style={{ color: KIND[kk].color }}>{KIND[kk].label} · {grp.length}</h5>
                                <div className="gd-top-grid">
                                    {grp.map((p) => <TopPostCard key={`${p._accountId}-${p.id}`} post={p} onOpen={onOpenPost} />)}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}

// ── agregación de un subconjunto de cuentas (un "bando": propias o de interés) ─
function aggregate(accounts, overviews) {
    const platforms = {};
    const themeMap = {};
    const flowMap = {};
    const ctMap = {};
    const perAccount = [];
    let followers = 0, growth = 0, interactions = 0, posts = 0;
    let analyzed = 0, pos = 0, neg = 0, views = 0;
    let erNum = 0, erDen = 0;

    for (const a of accounts) {
        const hasOv = Object.prototype.hasOwnProperty.call(overviews, a.id);
        const ov = overviews[a.id] || {};
        const rg = ov.reach_growth || {};
        const eng = ov.engagement || {};
        const sent = ov.sentiment || {};
        const plat = a.platform || 'otro';
        const fol = a.followers ?? rg.followers ?? 0;
        const accPosts = eng.posts || 0;
        const accEr = eng.avg_engagement_rate || 0;
        const accGrowth = rg.followers_growth || 0;
        const accViews = rg.total_views || 0;

        followers += fol || 0;
        growth += accGrowth || 0;
        interactions += (eng.total_likes || 0) + (eng.total_comments || 0);
        posts += accPosts;
        analyzed += sent.analyzed || 0;
        pos += sent.pos || 0;
        neg += sent.neg || 0;
        views += accViews || 0;
        if (accPosts > 0) { erNum += accEr * accPosts; erDen += accPosts; }

        if (!platforms[plat]) {
            platforms[plat] = {
                followers: 0, accounts: 0, posts: 0, views: 0, growth: 0, erNum: 0, erDen: 0,
                folMeasured: 0, accountsMeasured: 0, accountsPending: 0,
            };
        }
        const P = platforms[plat];
        P.followers += fol || 0; P.accounts += 1; P.posts += accPosts;
        P.views += accViews || 0; P.growth += accGrowth || 0;
        // Solo hay crecimiento MEDIBLE con ≥2 capturas en el rango. Se guarda aparte
        // la base de seguidores de esas cuentas: si se divide el crecimiento entre
        // el total de la plataforma, el % se diluye con cuentas que nadie midió.
        const measurable = hasOv && (rg.snapshots_in_range || 0) >= 2;
        if (measurable) { P.folMeasured += fol || 0; P.accountsMeasured += 1; }
        if (!hasOv) P.accountsPending += 1;
        if (accPosts > 0) { P.erNum += accEr * accPosts; P.erDen += accPosts; }

        (eng.by_content_type || []).forEach((t) => {
            const type = t.content_type || '?';
            if (!ctMap[type]) ctMap[type] = { type, posts: 0, erNum: 0, erDen: 0 };
            const C = ctMap[type];
            const p = t.posts || 0;
            C.posts += p;
            if (p > 0 && t.avg_er != null) { C.erNum += t.avg_er * p; C.erDen += p; }
        });

        (ov.themes || []).forEach((t) => {
            if (!t.theme) return;
            const key = t.theme.toLowerCase();
            if (!themeMap[key]) themeMap[key] = { theme: t.theme, n: 0, pos: 0, neg: 0 };
            themeMap[key].n += t.n || 0; themeMap[key].pos += t.pos || 0; themeMap[key].neg += t.neg || 0;
        });

        (ov.sentiment_flow || []).forEach((b) => {
            if (!b.bucket) return;
            if (!flowMap[b.bucket]) flowMap[b.bucket] = { pos: 0, neg: 0, analyzed: 0 };
            flowMap[b.bucket].pos += b.pos || 0;
            flowMap[b.bucket].neg += b.neg || 0;
            flowMap[b.bucket].analyzed += b.analyzed || 0;
        });

        perAccount.push({
            id: a.id, handle: a.username, platform: plat, kind: kindOf(a),
            followers: fol, er: accPosts > 0 ? accEr : null,
            nss: sent.analyzed ? sent.nss : null,
            posts: accPosts, growth: accGrowth, crisis: ov.crisis,
            pending: !hasOv,
        });
    }

    Object.values(platforms).forEach((P) => { P.er = P.erDen ? P.erNum / P.erDen : null; });

    const evolution = Object.keys(flowMap).sort().map((b) => {
        const f = flowMap[b];
        return {
            bucket: b,
            nss: f.analyzed ? round1(((f.pos - f.neg) / f.analyzed) * 100) : 0,
            volume: f.analyzed,
        };
    });

    const topThemes = Object.values(themeMap)
        .map((t) => ({ ...t, nss: t.n ? round1(((t.pos - t.neg) / t.n) * 100) : 0 }))
        .sort((a, b) => b.n - a.n)
        .slice(0, 6);

    const byContentType = Object.values(ctMap)
        .map((c) => ({ type: c.type, posts: c.posts, er: c.erDen ? round1(c.erNum / c.erDen) : 0 }))
        .sort((a, b) => b.posts - a.posts);

    return {
        platforms, evolution, topThemes, byContentType, perAccount,
        totals: {
            followers, growth, interactions, posts, analyzed, views,
            nss: analyzed ? round1(((pos - neg) / analyzed) * 100) : null,
            er: erDen ? round1(erNum / erDen) : null,
        },
    };
}

// ── "Crecimiento por plataforma" (visual Analytics CLMX 2026) ────────────────
// Total de followers por red + % de crecimiento del rango, con barra proporcional
// en el color de la marca. Se alimenta de lo que `aggregate` ya calcula
// (platforms[plat].followers / .growth), sin llamadas nuevas.
//
// El % es relativo al punto de partida de las cuentas REALMENTE medidas:
// growth / (folMeasured - growth). Usar el total de la plataforma diluiría el
// crecimiento con cuentas que solo tienen una captura y no se midieron.
// Sin ≥2 capturas en el rango no hay % (se muestra '—'), y un 0% medido sí se
// muestra como 0.0% para no confundirlo con "no medido".
function PlatformGrowthCard({ platforms }) {
    const rows = REAL_PLATFORMS
        .map((plat) => {
            const P = platforms?.[plat];
            if (!P || !P.followers) return null;
            const growth = P.growth || 0;
            const base = (P.folMeasured || 0) - growth;
            const medible = (P.accountsMeasured || 0) > 0 && base > 0;
            return {
                plat, meta: PLAT[plat], followers: P.followers, growth,
                pct: medible ? (growth / base) * 100 : null,
                // Cobertura: qué parte de los seguidores de la red entró en el cálculo.
                cobertura: P.followers > 0 ? (P.folMeasured || 0) / P.followers : 0,
                pendiente: (P.accountsPending || 0) > 0,
            };
        })
        .filter(Boolean)
        .sort((a, b) => b.followers - a.followers);

    if (rows.length === 0) return null;
    const max = Math.max(...rows.map((r) => r.followers));
    const best = rows.filter((r) => r.pct != null).sort((a, b) => b.pct - a.pct)[0];
    const sinDato = rows.filter((r) => r.pct == null).length;
    const parcial = rows.filter((r) => r.pct != null && r.cobertura < 0.99).length;

    return (
        <div className="gd-card">
            <div className="gd-card-head">
                <h4>Crecimiento por plataforma</h4>
                <span className="gd-muted-h">Total followers y % de crecimiento</span>
            </div>
            <div className="gd-platgrowth">
                {rows.map((r) => {
                    const Icon = r.meta.Icon;
                    return (
                        <div className="gd-pg-row" key={r.plat}>
                            <span className="gd-pg-icon" style={{ color: r.meta.color }}><Icon /></span>
                            <div className="gd-pg-body">
                                <div className="gd-pg-top">
                                    <span className="gd-pg-name">{r.meta.label}</span>
                                    <span className="gd-pg-val">{fmt(r.followers)}</span>
                                    <span
                                        className={`gd-pg-delta ${r.pct == null ? 'na' : r.pct >= 0 ? 'up' : 'down'}`}
                                        title={r.pct == null
                                            ? (r.pendiente ? 'Sin respuesta del overview de alguna cuenta.' : 'Requiere ≥2 capturas de la cuenta dentro del rango.')
                                            : (r.cobertura < 0.99 ? `Medido sobre el ${Math.round(r.cobertura * 100)}% de los seguidores de la red (el resto no tiene ≥2 capturas).` : undefined)}
                                    >
                                        {r.pct == null ? '—' : `${r.pct >= 0 ? '▲' : '▼'} ${round1(Math.abs(r.pct))}%`}
                                        {r.pct != null && r.cobertura < 0.99 && <sup className="gd-pg-flag">*</sup>}
                                    </span>
                                </div>
                                <div className="gd-pg-track">
                                    <div className="gd-pg-fill" style={{ width: `${(r.followers / max) * 100}%`, background: r.meta.color }} />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
            <div className="gd-pg-foot">
                <span className="gd-pg-foot-icon"><FaTrophy /></span>
                <div>
                    {/* Si el mejor valor es negativo, todas cayeron: el rótulo no puede decir
                        "mayor crecimiento" ni pintar una flecha verde hacia arriba. */}
                    <div className="gd-pg-foot-label">
                        {best && best.pct < 0 ? 'Plataforma con menor caída' : 'Plataforma con mayor crecimiento'}
                    </div>
                    <div className="gd-pg-foot-value">
                        {best ? `${best.meta.label}` : '—'}
                        {best && (
                            <span className={`gd-pg-delta ${best.pct >= 0 ? 'up' : 'down'}`}>
                                {' '}{best.pct >= 0 ? '▲' : '▼'} {round1(Math.abs(best.pct))}%
                            </span>
                        )}
                    </div>
                </div>
            </div>
            {(sinDato > 0 || parcial > 0) && (
                <p className="gd-note">
                    {sinDato > 0 && `${sinDato === 1 ? '1 plataforma no tiene' : `${sinDato} plataformas no tienen`} crecimiento calculable: requiere ≥2 capturas de la cuenta dentro del rango. `}
                    {parcial > 0 && `* El % de ${parcial === 1 ? '1 plataforma se midió' : `${parcial} plataformas se midieron`} solo sobre las cuentas con ≥2 capturas, no sobre el total de seguidores de la red.`}
                </p>
            )}
        </div>
    );
}

function computeAggByKind(accounts, overviews) {
    const own = accounts.filter((a) => kindOf(a) === 'own');
    const interest = accounts.filter((a) => kindOf(a) === 'interest');
    return {
        own: aggregate(own, overviews),
        interest: aggregate(interest, overviews),
        all: aggregate(accounts, overviews),
        counts: { own: own.length, interest: interest.length, total: accounts.length },
    };
}

export default function GeneralDashboard({ username }) {
    const [accounts, setAccounts] = useState([]);
    const [overviews, setOverviews] = useState({});
    const [postsByAcct, setPostsByAcct] = useState({});
    const [postsDone, setPostsDone] = useState(false);
    const [rangeDays, setRangeDays] = useState(90);
    const [benchMetric, setBenchMetric] = useState('followers');
    const [flowBucket, setFlowBucket] = useState('day');   // 'day' (default) | 'week'
    const [smooth, setSmooth] = useState(true);
    const [topFilter, setTopFilter] = useState('own');
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [streaming, setStreaming] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [err, setErr] = useState('');
    const [modalPost, setModalPost] = useState(null);
    const [modalAccountId, setModalAccountId] = useState(null);
    const [flowDay, setFlowDay] = useState(null);   // fecha clickeada en la gráfica NSS

    const reload = () => setReloadKey((k) => k + 1);

    // 1) Lista de cuentas (datos baratos: followers, plataforma, kind) — pinta al instante.
    useEffect(() => {
        if (!username) return undefined;
        let cancelled = false;
        setLoadingAccounts(true); setErr('');
        socialApi.listAccounts(username)
            .then((r) => { if (!cancelled) setAccounts(r.accounts || []); })
            .catch((e) => { if (!cancelled) setErr(e.message || 'No se pudo cargar la información'); })
            .finally(() => { if (!cancelled) setLoadingAccounts(false); });
        return () => { cancelled = true; };
    }, [username, reloadKey]);

    // 2) Overviews por cuenta en STREAMING (cada cuenta pinta al resolver).
    useEffect(() => {
        setOverviews({});
        if (!username || accounts.length === 0) { setStreaming(false); return undefined; }
        let cancelled = false;
        const from = dateFromDays(rangeDays);
        setStreaming(true);
        let settled = 0;
        const total = accounts.length;
        accounts.forEach((a) => {
            socialApi.overview(username, a.id, from, '', flowBucket)
                .then((r) => {
                    if (cancelled || !r?.overview) return;
                    setOverviews((prev) => ({ ...prev, [a.id]: r.overview }));
                })
                .catch(() => { /* una cuenta que falla no tumba el resto */ })
                .finally(() => {
                    if (cancelled) return;
                    settled += 1;
                    if (settled >= total) setStreaming(false);
                });
        });
        return () => { cancelled = true; };
        // Encadenado a `accounts` (data ya del usuario correcto): NO incluir `username`
        // en deps, o al cambiar de usuario dispararía con IDs de cuentas del anterior.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, rangeDays, flowBucket]);

    // 2b) Posts por cuenta en streaming (para "Mejores publicaciones" + miniaturas).
    useEffect(() => {
        setPostsByAcct({}); setPostsDone(false);
        if (!username || accounts.length === 0) { setPostsDone(true); return undefined; }
        let cancelled = false;
        const from = dateFromDays(rangeDays);
        let settled = 0;
        const total = accounts.length;
        accounts.forEach((a) => {
            socialApi.posts(username, a.id, from, '')
                .then((r) => {
                    if (cancelled || !r?.posts) return;
                    setPostsByAcct((prev) => ({ ...prev, [a.id]: r.posts }));
                })
                .catch(() => { /* una cuenta que falla no tumba el resto */ })
                .finally(() => {
                    if (cancelled) return;
                    settled += 1;
                    if (settled >= total) setPostsDone(true);   // completado aunque haya errores
                });
        });
        return () => { cancelled = true; };
        // Mismo motivo que el efecto de overviews: encadenado a `accounts`, sin `username`.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [accounts, rangeDays]);

    const agg = useMemo(() => computeAggByKind(accounts, overviews), [accounts, overviews]);
    const { own, interest, all, counts } = agg;

    // ── Mejores publicaciones (todas las cuentas, etiquetadas por kind) ──────
    const allPosts = useMemo(() => {
        const out = [];
        accounts.forEach((a) => {
            (postsByAcct[a.id] || []).forEach((p) => {
                out.push({ ...p, _handle: a.username, _platform: a.platform, _kind: kindOf(a), _accountId: a.id });
            });
        });
        return out;
    }, [accounts, postsByAcct]);
    const topPosts = useMemo(() => [...allPosts]
        .filter((p) => topFilter === 'all' || p._kind === topFilter)
        .sort((a, b) => (b.er ?? -1) - (a.er ?? -1))
        .slice(0, 8), [allPosts, topFilter]);

    const hasAccounts = accounts.length > 0;
    const hasInterest = counts.interest > 0;
    const ownReady = own.totals.analyzed > 0;
    const intReady = interest.totals.analyzed > 0;
    const readyCount = Object.keys(overviews).length;

    // ── Scorecard competitivo (gap cards Propias vs De interés) ──────────────
    const cards = [
        { key: 'followers', label: 'Seguidores', icon: <FaUsers />, own: own.totals.followers, interest: interest.totals.followers, kind: 'num' },
        { key: 'er', label: 'Engagement rate', icon: <FaChartLine />, own: own.totals.er, interest: interest.totals.er, kind: 'pct' },
        { key: 'views', label: 'Alcance video', icon: <FaBullhorn />, own: own.totals.views, interest: interest.totals.views, kind: 'num' },
        { key: 'nss', label: 'NSS (sentimiento)', icon: <FaComments />, own: own.totals.nss, interest: interest.totals.nss, kind: 'nss', guard: !(ownReady && intReady) },
    ];
    const dispVal = (v, kind) => (v == null ? '—' : kind === 'num' ? fmt(v) : kind === 'pct' ? `${v}%` : String(v));
    const verdictOf = (c) => {
        if (c.guard) return { cls: 'muted', text: 'Sin análisis' };
        if (!hasInterest) return { cls: 'muted', text: 'Marca competidores' };
        if (c.own == null || c.interest == null) return { cls: 'muted', text: '—' };
        const d = c.own - c.interest;
        const mag = c.kind === 'num' ? fmt(Math.abs(d)) : c.kind === 'pct' ? `${round1(Math.abs(d))} pp` : `${Math.abs(Math.round(d))} pts`;
        if (d === 0) return { cls: 'muted', text: 'Empate' };
        return { cls: d > 0 ? 'win' : 'lose', text: `${d > 0 ? '▲' : '▼'} ${mag}` };
    };
    // Proporción Propias/De interés para la barrita del scorecard.
    const shareOwn = (c) => {
        const o = Math.abs(c.own || 0), i = Math.abs(c.interest || 0);
        return o + i > 0 ? Math.round((o / (o + i)) * 100) : 50;
    };

    // ── Benchmark por cuenta ─────────────────────────────────────────────────
    const benchVal = (p) => (benchMetric === 'followers' ? (p.followers || 0) : benchMetric === 'er' ? (p.er || 0) : (p.growth || 0));
    const benchAccounts = [...all.perAccount].sort((a, b) => benchVal(b) - benchVal(a));
    const benchLabel = { followers: 'Seguidores', er: 'Engagement rate (%)', growth: 'Crecimiento de seguidores' }[benchMetric];

    // ── Sentimiento en el tiempo (dual line suavizada, con guards) ───────────
    const sentBuckets = Array.from(new Set([
        ...own.evolution.map((b) => b.bucket), ...interest.evolution.map((b) => b.bucket),
    ])).sort();
    const ownFlow = Object.fromEntries(own.evolution.map((b) => [b.bucket, b.nss]));
    const intFlow = Object.fromEntries(interest.evolution.map((b) => [b.bucket, b.nss]));
    const ownSeries = sentBuckets.map((b) => (b in ownFlow ? ownFlow[b] : null));
    const intSeries = sentBuckets.map((b) => (b in intFlow ? intFlow[b] : null));
    const ownDraw = smooth ? movingAvg(ownSeries, 3) : ownSeries;
    const intDraw = smooth ? movingAvg(intSeries, 3) : intSeries;

    // ── Tipo de contenido (Propias vs De interés) ────────────────────────────
    const ctTypes = Array.from(new Set([
        ...own.byContentType.map((c) => c.type), ...interest.byContentType.map((c) => c.type),
    ]));
    const ownCT = Object.fromEntries(own.byContentType.map((c) => [c.type, c.er]));
    const intCT = Object.fromEntries(interest.byContentType.map((c) => [c.type, c.er]));

    // ── Ranking (por engagement rate; honesto sobre datos faltantes) ─────────
    const ranking = [...all.perAccount].sort((a, b) => (b.er ?? -1) - (a.er ?? -1));

    // ── Alertas: crisis en propias (atender) / oportunidad en interés ────────
    const ownAlerts = all.perAccount.filter((p) => p.kind === 'own' && (p.crisis?.alert || (p.nss != null && p.nss < 0)));
    const intAlerts = all.perAccount.filter((p) => p.kind === 'interest' && (p.crisis?.alert || (p.nss != null && p.nss < 0)));

    // ── Análisis competitivo (derivado, con guards de sentimiento) ───────────
    const cInsights = [];
    if (hasInterest) {
        const fd = own.totals.followers - interest.totals.followers;
        cInsights.push({
            icon: fd >= 0 ? <FaCheckCircle /> : <FaExclamationTriangle />, color: fd >= 0 ? '#16a34a' : '#d97706',
            text: fd >= 0 ? `Aventajas al conjunto de interés por ${fmt(fd)} seguidores.` : `El conjunto de interés te supera por ${fmt(Math.abs(fd))} seguidores.`,
        });
        if (own.totals.er != null && interest.totals.er != null) {
            const ed = round1(own.totals.er - interest.totals.er);
            cInsights.push({
                icon: ed >= 0 ? <FaCheckCircle /> : <FaExclamationTriangle />, color: ed >= 0 ? '#16a34a' : '#d97706',
                text: ed >= 0 ? `Tu engagement rate va ${ed} pp por ENCIMA de la competencia.` : `Tu engagement rate va ${Math.abs(ed)} pp por DEBAJO de la competencia.`,
            });
        }
        if (ownReady && intReady) {
            const nd = Math.round(own.totals.nss - interest.totals.nss);
            cInsights.push({
                icon: nd >= 0 ? <FaCheckCircle /> : <FaExclamationTriangle />, color: nssColor(nd),
                text: nd >= 0 ? `Tu sentimiento (NSS) supera al de interés por ${nd} pts.` : `El sentimiento de interés te supera por ${Math.abs(nd)} pts.`,
            });
        } else {
            cInsights.push({ icon: <FaExclamationTriangle />, color: '#94a3b8', text: 'Sentimiento sin comparar: falta analizar comentarios en alguno de los dos grupos.' });
        }
    } else {
        cInsights.push({ icon: <FaEye />, color: KIND.interest.color, text: 'Marca tus competidores como “De interés” en Redes Sociales para activar la comparación.' });
    }

    const ownBest = all.perAccount.filter((p) => p.kind === 'own' && p.er != null).sort((a, b) => b.er - a.er)[0];
    const intBest = all.perAccount.filter((p) => p.kind === 'interest' && p.er != null).sort((a, b) => b.er - a.er)[0];
    let reco = null;
    if (intBest && (!ownBest || intBest.er > ownBest.er)) {
        reco = `@${intBest.handle} (de interés) lidera el engagement (${intBest.er}%). Estudia su formato de contenido y adáptalo.`;
    } else if (ownBest) {
        reco = `@${ownBest.handle} es tu cuenta con mejor engagement (${ownBest.er}%). Escala ese formato al resto de tus cuentas.`;
    }

    // ── Donut Propias vs De interés (seguidores) ─────────────────────────────
    const kindDonut = [
        { label: 'Propias', value: own.totals.followers, color: KIND.own.color },
        { label: 'De interés', value: interest.totals.followers, color: KIND.interest.color },
    ].filter((d) => d.value > 0);

    const noAnim = streaming ? false : undefined;
    const modalAccount = modalAccountId != null ? accounts.find((a) => a.id === modalAccountId) : null;

    return (
        <div className="gd-root">
            {/* Header */}
            <div className="gd-head">
                <div>
                    <h2 className="gd-title">Análisis competencia</h2>
                    <p className="gd-subtitle">Benchmarking competitivo: tus cuentas <b style={{ color: KIND.own.color }}>Propias</b> vs. las <b style={{ color: KIND.interest.color }}>De interés</b></p>
                    {hasAccounts && (
                        <div className="gd-coverage">
                            <span><FaUserCheck style={{ color: KIND.own.color }} /> {counts.own} propias</span>
                            <span><FaEye style={{ color: KIND.interest.color }} /> {counts.interest} de interés</span>
                            <span><FaCommentDots /> {fmt(all.totals.analyzed)} comentarios analizados</span>
                        </div>
                    )}
                </div>
                <div className="gd-head-actions">
                    <select value={rangeDays} onChange={(e) => setRangeDays(Number(e.target.value))} style={{ cursor: 'pointer' }}>
                        {RANGES.map((r) => <option key={r.days} value={r.days}>{r.label}</option>)}
                    </select>
                    {!SOCIAL_READONLY && (
                        <button onClick={reload} disabled={loadingAccounts || streaming}
                            style={{ cursor: loadingAccounts || streaming ? 'default' : 'pointer' }} title="Actualizar">
                            <FaSync className={loadingAccounts || streaming ? 'spin' : ''} /> Actualizar
                        </button>
                    )}
                </div>
            </div>

            {err && <div className="gd-card" style={{ borderColor: '#fecaca', background: '#fef2f2', color: '#b91c1c', marginBottom: 16 }}>{err}</div>}
            {!username && <div className="gd-card">Inicia sesión para ver el dashboard.</div>}

            {username && hasAccounts && streaming && (
                <div className="gd-streaming"><FaSync className="spin" /> Cargando métricas… {readyCount}/{accounts.length} cuentas</div>
            )}

            {username && !hasAccounts && !loadingAccounts && (
                <div className="gd-card">
                    <h4>Aún no hay cuentas monitoreadas</h4>
                    <p className="gd-subtitle" style={{ margin: 0 }}>
                        Agrega cuentas de Instagram o Facebook en la pestaña <b>Redes Sociales</b> (propias y de interés) para activar el benchmarking.
                    </p>
                </div>
            )}

            {username && hasAccounts && !hasInterest && (
                <div className="gd-cta">
                    <FaEye />
                    <div>
                        <b>Activa el benchmarking competitivo</b>
                        <p>Aún no marcas ninguna cuenta como <b>De interés</b>. En la pestaña <b>Redes Sociales</b> agrega o reclasifica a tus competidores como “De interés” y este panel los comparará contra tus cuentas automáticamente.</p>
                    </div>
                </div>
            )}

            {username && hasAccounts && (
                <>
                    {/* Scorecard competitivo */}
                    <div className="gd-vs-cards">
                        {cards.map((c) => {
                            const v = verdictOf(c);
                            const showBar = !c.guard && hasInterest && c.own != null && c.interest != null && c.kind !== 'nss';
                            return (
                                <div className="gd-vs-card" key={c.key}>
                                    <div className="gd-vs-head"><span className="gd-vs-ic">{c.icon}</span> {c.label}</div>
                                    <div className="gd-vs-row">
                                        <div className="gd-vs-side">
                                            <span className="gd-vs-lbl" style={{ color: KIND.own.color }}>Propias</span>
                                            <span className="gd-vs-num">{dispVal(c.own, c.kind)}</span>
                                        </div>
                                        <span className="gd-vs-sep">vs</span>
                                        <div className="gd-vs-side gd-vs-right">
                                            <span className="gd-vs-lbl" style={{ color: KIND.interest.color }}>De interés</span>
                                            <span className="gd-vs-num">{hasInterest ? dispVal(c.interest, c.kind) : '—'}</span>
                                        </div>
                                    </div>
                                    {showBar && (
                                        <div className="gd-vs-bar" title="Proporción Propias vs De interés">
                                            <div style={{ width: `${shareOwn(c)}%`, background: KIND.own.color }} />
                                            <div style={{ width: `${100 - shareOwn(c)}%`, background: KIND.interest.color }} />
                                        </div>
                                    )}
                                    <div className={`gd-vs-verdict ${v.cls}`}>{v.text}</div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Cuerpo: main + rail */}
                    <div className="gd-grid">
                        <div className="gd-main">
                            {/* Sentimiento en el tiempo (suavizado) */}
                            <div className="gd-card">
                                <div className="gd-card-head">
                                    <h4>Sentimiento en el tiempo (NSS) <span className="gd-muted-h">Propias vs De interés</span></h4>
                                    <div className="gd-flow-controls">
                                        <div className="gd-seg">
                                            <button className={flowBucket === 'week' ? 'active' : ''} onClick={() => setFlowBucket('week')}>Semana</button>
                                            <button className={flowBucket === 'day' ? 'active' : ''} onClick={() => setFlowBucket('day')}>Día</button>
                                        </div>
                                        <button className={`gd-smooth ${smooth ? 'active' : ''}`} onClick={() => setSmooth((s) => !s)}>
                                            <FaChartLine /> Suavizar
                                        </button>
                                    </div>
                                </div>
                                <div className="gd-chart">
                                    {(!ownReady && !intReady) ? (
                                        <div className="gd-empty-guard">
                                            <FaCommentDots />
                                            <p>Sin comentarios analizados. En <b>Redes Sociales</b> abre un post → <b>Capturar comentarios</b> → <b>Analizar sentimiento</b> para poblar esta tendencia.</p>
                                        </div>
                                    ) : (
                                        <>
                                            <Line
                                                data={{
                                                    labels: sentBuckets.map((b) => (b || '').slice(0, 10)),
                                                    datasets: [
                                                        { label: 'Propias', data: ownDraw, borderColor: KIND.own.color, backgroundColor: 'rgba(7,60,92,.10)', fill: true, tension: smooth ? 0.45 : 0.3, pointRadius: 2, borderWidth: 2, spanGaps: true },
                                                        { label: 'De interés', data: intDraw, borderColor: KIND.interest.color, backgroundColor: 'transparent', fill: false, tension: smooth ? 0.45 : 0.3, pointRadius: 2, borderWidth: 2, borderDash: [5, 3], spanGaps: true },
                                                    ],
                                                }}
                                                options={{
                                                    responsive: true, maintainAspectRatio: false, animation: noAnim,
                                                    interaction: { mode: 'index', intersect: false },
                                                    onClick: (evt, els) => { if (els.length) setFlowDay((sentBuckets[els[0].index] || '').slice(0, 10)); },
                                                    onHover: (evt, els) => { const t = evt?.native?.target; if (t) t.style.cursor = els.length ? 'pointer' : 'default'; },
                                                    plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } } },
                                                    scales: { y: { min: -100, max: 100, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
                                                }}
                                            />
                                            <p className="gd-note">Haz click en un punto para ver las publicaciones de esa fecha.</p>
                                            {!intReady && <p className="gd-note">Cuentas de interés sin análisis de sentimiento — su línea no se dibuja (no es 0 real).</p>}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* Mejores publicaciones (miniaturas) */}
                            <div className="gd-card">
                                <div className="gd-card-head">
                                    <h4><FaFire style={{ color: ORANGE }} /> Mejores publicaciones <span className="gd-muted-h">por engagement</span></h4>
                                    <div className="gd-seg">
                                        {[['own', 'Propias'], ['interest', 'De interés'], ['all', 'Todas']].map(([k, lbl]) => (
                                            <button key={k} className={topFilter === k ? 'active' : ''} onClick={() => setTopFilter(k)}>{lbl}</button>
                                        ))}
                                    </div>
                                </div>
                                {topPosts.length === 0 ? (
                                    <p className="gd-subtitle">{!postsDone ? 'Cargando publicaciones…' : 'Sin publicaciones en el rango.'}</p>
                                ) : (
                                    <div className="gd-top-grid">
                                        {topPosts.map((p) => <TopPostCard key={`${p._accountId}-${p.id}`} post={p} onOpen={setModalPost} />)}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Rail — junto a Sentimiento y Mejores publicaciones */}
                        <aside className="gd-rail">
                            {/* Donut Propias vs De interés */}
                            <div className="gd-card">
                                <h4>Seguidores: Propias vs De interés</h4>
                                <div className="gd-donut">
                                    {kindDonut.length === 0 ? <p className="gd-subtitle">Sin datos de seguidores aún.</p> : (
                                        <Doughnut
                                            data={{ labels: kindDonut.map((d) => d.label), datasets: [{ data: kindDonut.map((d) => d.value), backgroundColor: kindDonut.map((d) => d.color), borderWidth: 0 }] }}
                                            options={{ responsive: true, maintainAspectRatio: false, cutout: '62%', animation: noAnim, plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8, padding: 12 } } } }}
                                        />
                                    )}
                                </div>
                            </div>

                            {/* Centro de alertas: crisis (atender) / oportunidad */}
                            <div className="gd-card">
                                <div className="gd-alerts-head">
                                    <h4>Centro de alertas</h4>
                                    <span className="gd-alerts-count">{ownAlerts.length + intAlerts.length}</span>
                                </div>
                                {(ownAlerts.length + intAlerts.length) === 0 ? (
                                    <div className="gd-alert">
                                        <span className="gd-alert-ic" style={{ color: '#16a34a' }}><FaCheckCircle /></span>
                                        <div><b>Sin alertas</b><span>{(ownReady || intReady) ? 'No se detectaron caídas de sentimiento' : 'Analiza comentarios para activar alertas de crisis'}</span></div>
                                    </div>
                                ) : (
                                    <>
                                        {ownAlerts.map((p) => (
                                            <div className="gd-alert gd-row-click" key={`o${p.id}`} onClick={() => setModalAccountId(p.id)}>
                                                <span className="gd-alert-ic" style={{ color: '#dc2626' }}><FaExclamationTriangle /></span>
                                                <div><b>Atender · @{p.handle}</b><span>{p.crisis?.alert ? `Sentimiento cayó ${p.crisis.nss_drop} pts (NSS ${p.crisis.last_nss})` : `NSS negativo (${p.nss})`}</span></div>
                                            </div>
                                        ))}
                                        {intAlerts.map((p) => (
                                            <div className="gd-alert gd-row-click" key={`i${p.id}`} onClick={() => setModalAccountId(p.id)}>
                                                <span className="gd-alert-ic" style={{ color: '#d97706' }}><FaLightbulb /></span>
                                                <div><b>Oportunidad · @{p.handle}</b><span>{p.crisis?.alert ? `Competidor con crisis (NSS ${p.crisis.last_nss})` : `Competidor con NSS negativo (${p.nss})`}</span></div>
                                            </div>
                                        ))}
                                    </>
                                )}
                            </div>

                            {/* Análisis competitivo (AI) */}
                            <div className="gd-card gd-ai">
                                <div className="gd-ai-head">
                                    <span className="gd-ai-badge"><FaRobot /> AI</span>
                                    <span className="gd-tag">BETA</span>
                                </div>
                                <h5>Análisis competitivo</h5>
                                {cInsights.map((a, i) => (
                                    <div className="gd-ai-row" key={i}>
                                        <span style={{ color: a.color }}>{a.icon}</span>
                                        <p>{a.text}</p>
                                    </div>
                                ))}
                                {reco && (
                                    <div className="gd-ai-reco">
                                        <FaLightbulb />
                                        <div><b>Recomendación principal</b><p>{reco}</p></div>
                                    </div>
                                )}
                            </div>
                        </aside>
                    </div>

                    {/* Gráficas y tablas a ancho completo (desde Benchmark) */}
                    <div className="gd-full">
                            <PlatformGrowthCard platforms={own.platforms} />

                            {/* Benchmark por cuenta */}
                            <div className="gd-card">
                                <div className="gd-card-head">
                                    <h4>Benchmark por cuenta — {benchLabel}</h4>
                                    <div className="gd-seg">
                                        {[['followers', 'Seguidores'], ['er', 'Engagement'], ['growth', 'Crecimiento']].map(([k, lbl]) => (
                                            <button key={k} className={benchMetric === k ? 'active' : ''} onClick={() => setBenchMetric(k)}>{lbl}</button>
                                        ))}
                                    </div>
                                </div>
                                <div className="gd-legend">
                                    <span><i style={{ background: KIND.own.color }} /> Propias</span>
                                    <span><i style={{ background: KIND.interest.color }} /> De interés</span>
                                    <span className="gd-muted-h">· clic en una barra para ver el detalle</span>
                                </div>
                                <div className="gd-chart">
                                    {benchAccounts.length === 0 ? <p className="gd-subtitle">Sin cuentas.</p> : (
                                        <Bar
                                            data={{
                                                labels: benchAccounts.map((p) => `@${p.handle}`),
                                                datasets: [{
                                                    label: benchLabel,
                                                    data: benchAccounts.map((p) => benchVal(p)),
                                                    backgroundColor: benchAccounts.map((p) => KIND[p.kind].color),
                                                    borderRadius: 6,
                                                }],
                                            }}
                                            options={{
                                                responsive: true, maintainAspectRatio: false, animation: noAnim,
                                                onClick: (evt, els) => { if (els.length) setModalAccountId(benchAccounts[els[0].index].id); },
                                                onHover: (evt, els) => { const t = evt?.native?.target; if (t) t.style.cursor = els.length ? 'pointer' : 'default'; },
                                                plugins: { legend: { display: false } },
                                                scales: { y: { beginAtZero: true, grid: { color: '#f1f5f9' } }, x: { grid: { display: false } } },
                                            }}
                                        />
                                    )}
                                </div>
                                {benchMetric === 'growth' && (
                                    <p className="gd-note">El crecimiento requiere ≥2 capturas de la cuenta en el rango; con una sola captura aparece en 0.</p>
                                )}
                            </div>

                            {/* Ranking competitivo */}
                            <div className="gd-card">
                                <h4><FaTrophy style={{ color: ORANGE }} /> Ranking competitivo <span className="gd-muted-h">(por engagement rate · clic para detalle)</span></h4>
                                <div className="gd-table-wrap">
                                    <table className="gd-table">
                                        <thead>
                                            <tr><th>#</th><th>Cuenta</th><th>Tipo</th><th>Red</th><th>Seguidores</th><th>Crec.</th><th>ER</th><th>NSS</th><th>Posts</th></tr>
                                        </thead>
                                        <tbody>
                                            {ranking.map((p, i) => {
                                                const meta = PLAT[p.platform] || {};
                                                const Icon = meta.Icon || FaGlobe;
                                                const waiting = p.pending && streaming;
                                                const wait = <span className="gd-cell-pending">···</span>;
                                                return (
                                                    <tr key={p.id} className="gd-row-click" onClick={() => setModalAccountId(p.id)}>
                                                        <td className="gd-rank">{i + 1}</td>
                                                        <td className="gd-strong">@{p.handle}</td>
                                                        <td><span className="gd-kind-pill" style={{ color: KIND[p.kind].color, borderColor: KIND[p.kind].color }}>{KIND[p.kind].label}</span></td>
                                                        <td><Icon style={{ color: meta.color }} /></td>
                                                        <td>{fmt(p.followers)}</td>
                                                        <td className={!waiting && p.growth > 0 ? 'gd-pos' : !waiting && p.growth < 0 ? 'gd-neg' : ''}>
                                                            {waiting ? wait : (p.growth ? `${p.growth > 0 ? '▲' : '▼'} ${fmt(Math.abs(p.growth))}` : '—')}
                                                        </td>
                                                        <td>{waiting ? wait : (p.er != null ? `${p.er}%` : '—')}</td>
                                                        <td style={{ color: waiting ? undefined : nssColor(p.nss), fontWeight: 700 }}>{waiting ? wait : (p.nss != null ? p.nss : '—')}</td>
                                                        <td>{waiting ? wait : fmt(p.posts)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>

                            {/* Tipo de contenido */}
                            <div className="gd-card">
                                <h4>Engagement por tipo de contenido — Propias vs De interés</h4>
                                <div className="gd-chart">
                                    {ctTypes.length === 0 ? <p className="gd-subtitle">Sin posts en el rango.</p> : (
                                        <Bar
                                            data={{
                                                labels: ctTypes,
                                                datasets: [
                                                    { label: 'Propias', data: ctTypes.map((t) => ownCT[t] ?? 0), backgroundColor: KIND.own.color, borderRadius: 6 },
                                                    { label: 'De interés', data: ctTypes.map((t) => intCT[t] ?? 0), backgroundColor: KIND.interest.color, borderRadius: 6 },
                                                ],
                                            }}
                                            options={{
                                                responsive: true, maintainAspectRatio: false, animation: noAnim,
                                                plugins: { legend: { position: 'bottom', labels: { usePointStyle: true, boxWidth: 8 } } },
                                                scales: { y: { beginAtZero: true, grid: { color: '#f1f5f9' }, title: { display: true, text: 'ER %' } }, x: { grid: { display: false } } },
                                            }}
                                        />
                                    )}
                                </div>
                                <p className="gd-note">Se capturan ~40 posts por cuenta, así que algunos formatos pueden tener pocas muestras.</p>
                            </div>

                            {/* Performance por canal split por tipo */}
                            <div className="gd-card">
                                <h4>Performance por canal — Propias vs De interés</h4>
                                <div className="gd-table-wrap">
                                    <table className="gd-table">
                                        <thead><tr><th>Canal</th><th>Tipo</th><th>Seguidores</th><th>Views</th><th>ER</th></tr></thead>
                                        <tbody>
                                            {REAL_PLATFORMS.flatMap((plat) => {
                                                const meta = PLAT[plat];
                                                const Icon = meta.Icon;
                                                const O = own.platforms[plat];
                                                const I = interest.platforms[plat];
                                                if (!O && !I) return [];
                                                return [
                                                    <tr key={`${plat}-own`}>
                                                        <td rowSpan={2} className="gd-strong"><Icon style={{ color: meta.color }} /> {meta.label}</td>
                                                        <td style={{ color: KIND.own.color }}>Propias</td>
                                                        <td>{fmt(O?.followers)}</td>
                                                        <td>{O?.views ? fmt(O.views) : '—'}</td>
                                                        <td>{O?.er != null ? `${round1(O.er)}%` : '—'}</td>
                                                    </tr>,
                                                    <tr key={`${plat}-int`}>
                                                        <td style={{ color: KIND.interest.color }}>De interés</td>
                                                        <td>{hasInterest ? fmt(I?.followers) : '—'}</td>
                                                        <td>{I?.views ? fmt(I.views) : '—'}</td>
                                                        <td>{I?.er != null ? `${round1(I.er)}%` : '—'}</td>
                                                    </tr>,
                                                ];
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                    </div>
                </>
            )}

            {flowDay && (
                <DayPostsModal
                    date={flowDay} bucket={flowBucket} posts={allPosts}
                    onOpenPost={(p) => { setFlowDay(null); setModalPost(p); }}
                    onClose={() => setFlowDay(null)}
                />
            )}
            {modalPost && <PostModal username={username} post={modalPost} onClose={() => setModalPost(null)} />}
            {modalAccount && (
                <AccountModal
                    account={modalAccount}
                    overview={overviews[modalAccount.id]}
                    posts={postsByAcct[modalAccount.id]}
                    noAnim={noAnim}
                    onOpenPost={(p) => { setModalAccountId(null); setModalPost(p); }}
                    onClose={() => setModalAccountId(null)}
                />
            )}
        </div>
    );
}
